import PropTypes from "prop-types";
import React from "react";
import { Menu } from "@headlessui/react";
import TableActionMenuWrapper from "../lib/Shared/TableActionMenuWrapper";
import DeleteButtonWithConfirmation from "../lib/Shared/DeleteButtonWithConfirmation";
import ComplianceTemplateOverview from "./ComplianceTemplateOverview";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { formatDateDisplay } from "./utils";

const TemplatesTable = ({
  templates,
  selectedTemplateId,
  onView,
  onEdit,
  onClose,
  templatePanelMode,
  selectedTemplate,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
  canDelete = true,
  TemplateEditor, // Passed as a prop to avoid circular dependency
}) => {
  const isViewingTemplate =
    Boolean(selectedTemplate) && templatePanelMode === "view";
  const isEditingTemplate =
    Boolean(selectedTemplate) && templatePanelMode === "edit";

  const DELETE_TEMPLATE_MODAL = "delete_template_modal";

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2">Template</th>
            <th className="px-4 py-2">Owner</th>
            <th className="px-4 py-2 text-right">Version</th>
            <th className="px-4 py-2 text-right">Categories</th>
            <th className="px-4 py-2 text-right">Created</th>
            <th className="px-4 py-2 text-right">Updated</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {templates.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-3 text-sm text-gray-500">
                No templates created yet.
              </td>
            </tr>
          ) : (
            templates.map((template) => {
              const isSelected = selectedTemplateId === template.id;
              const categories = template.categories || [];
              const categoryCount = categories.length;
              const totalWeight = categories.reduce(
                (sum, category) => sum + Number(category.weight || 0),
                0
              );
              const normalizedTotal = Number(totalWeight.toFixed(2));
              const hasImbalance =
                categoryCount > 0 && Math.abs(normalizedTotal - 100) > 0.01;
              const showEditorOrOverview =
                isSelected && (isViewingTemplate || isEditingTemplate);

              return (
                <React.Fragment key={template.id}>
                  <tr
                    className={`transition hover:bg-slate-50 ${
                      isSelected ? "bg-sflPrimary/5" : ""
                    }`}
                  >
                    <td className="px-4 py-2 font-medium text-gray-700">
                      <div className="flex flex-col">
                        <span>{template.name}</span>
                        {template.description && (
                          <span className="text-xs text-gray-500">
                            {template.description}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {template.ownerType === "superAdmin"
                        ? "SuperAdmin"
                        : "OrgAdmin"}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {template.version}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      <span className="inline-flex items-center justify-end gap-1">
                        <span>{categoryCount}</span>
                        {hasImbalance && (
                          <span
                            className="inline-flex"
                            title="Category weights do not total 100%. Edit this template to adjust category weights."
                          >
                            <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">
                      {formatDateDisplay(template.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">
                      {formatDateDisplay(template.updatedAt)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end">
                        <TableActionMenuWrapper>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => onView(template.id)}
                                className={`${
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700"
                                } block w-full px-4 py-2 text-left text-sm`}
                              >
                                View
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => onEdit(template.id)}
                                className={`${
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700"
                                } block w-full px-4 py-2 text-left text-sm`}
                              >
                                Edit
                              </button>
                            )}
                          </Menu.Item>
                          {canDelete && (
                            <Menu.Item>
                              {({ active }) => (
                                <DeleteButtonWithConfirmation
                                  row={template}
                                  confirmationModalName={DELETE_TEMPLATE_MODAL}
                                  btnLabel="Delete"
                                  overrideBaseStyles
                                  btnClasses={`${
                                    active
                                      ? "bg-red-50 text-red-600"
                                      : "text-red-600"
                                  } block w-full px-4 py-2 text-left text-sm`}
                                />
                              )}
                            </Menu.Item>
                          )}
                          {isSelected && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => onClose(template.id)}
                                  className={`${
                                    active
                                      ? "bg-gray-100 text-gray-900"
                                      : "text-gray-700"
                                  } block w-full px-4 py-2 text-left text-sm`}
                                >
                                  Close
                                </button>
                              )}
                            </Menu.Item>
                          )}
                        </TableActionMenuWrapper>
                      </div>
                    </td>
                  </tr>
                  {showEditorOrOverview && (
                    <tr className="bg-gray-50/50">
                      <td colSpan={7} className="px-6 py-8">
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                          <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {isViewingTemplate ? "Viewing" : "Editing"}{" "}
                              Template: {template.name}
                            </h4>
                            <button
                              type="button"
                              onClick={() => onClose(template.id)}
                              className="text-gray-400 hover:text-gray-600"
                              aria-label="Close"
                            >
                              <span className="text-2xl leading-none">
                                &times;
                              </span>
                            </button>
                          </div>

                          {isViewingTemplate && (
                            <ComplianceTemplateOverview
                              categories={selectedTemplate.categories}
                            />
                          )}

                          {isEditingTemplate && TemplateEditor && (
                            <TemplateEditor
                              template={selectedTemplate}
                              frequencyOptions={frequencyOptions}
                              timelineTypes={timelineTypes}
                              onUpdateCategory={onUpdateCategory}
                              onAddCategory={onAddCategory}
                              onRemoveCategory={onRemoveCategory}
                              onUpdateTask={onUpdateTask}
                              onAddTask={onAddTask}
                              onRemoveTask={onRemoveTask}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

TemplatesTable.propTypes = {
  templates: PropTypes.array.isRequired,
  selectedTemplateId: PropTypes.string,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  templatePanelMode: PropTypes.oneOf(["view", "edit"]),
  selectedTemplate: PropTypes.object,
  frequencyOptions: PropTypes.array,
  timelineTypes: PropTypes.object,
  onUpdateCategory: PropTypes.func,
  onAddCategory: PropTypes.func,
  onRemoveCategory: PropTypes.func,
  onUpdateTask: PropTypes.func,
  onAddTask: PropTypes.func,
  onRemoveTask: PropTypes.func,
  canDelete: PropTypes.bool,
  TemplateEditor: PropTypes.elementType,
};

TemplatesTable.defaultProps = {
  selectedTemplateId: null,
  templatePanelMode: "view",
  selectedTemplate: null,
  frequencyOptions: [],
  timelineTypes: {},
  onUpdateCategory: () => {},
  onAddCategory: () => {},
  onRemoveCategory: () => {},
  onUpdateTask: () => {},
  onAddTask: () => {},
  onRemoveTask: () => {},
  canDelete: true,
};

export default TemplatesTable;
